const isLocalhost = Boolean(
  window.location.hostname === "localhost" ||
    window.location.hostname === "[::1]" ||
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/),
);

/** Clear Cache Storage + unregister every non-push service worker. */
export async function unregister() {
  if (typeof window === "undefined") return;
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch {
    /* ignore */
  }
  if (!("serviceWorker" in navigator)) return;
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      registrations.map((registration) => {
        const script =
          registration.active?.scriptURL ||
          registration.installing?.scriptURL ||
          registration.waiting?.scriptURL ||
          "";
        if (script.includes("push-sw.js")) return Promise.resolve();
        return registration.unregister();
      }),
    );
  } catch {
    /* ignore */
  }
}

/** Register the push-only service worker (safe on localhost for testing). */
export async function registerPushServiceWorker() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return null;
  try {
    await unregister();
    const reg = await navigator.serviceWorker.register(`${process.env.PUBLIC_URL || ""}/push-sw.js`, {
      scope: "/",
    });
    return reg;
  } catch (err) {
    if (isLocalhost) {
      console.warn("Push service worker registration failed", err);
    }
    return null;
  }
}

/**
 * Watch for a new DigitalOcean deploy and soft-reload once.
 * Compares baked-in REACT_APP_BUILD_ID with /build-meta.json.
 *
 * Do not poll on a short timer: DevTools focus/visibility storms would
 * fetch it again and again on every page. Check once after load, then
 * at most every 10 minutes (or on tab focus, still gated by that gap).
 */
export function startBuildUpdateWatcher() {
  if (process.env.NODE_ENV !== "production") return;
  const currentBuildId = process.env.REACT_APP_BUILD_ID;
  if (!currentBuildId) return;

  const RELOAD_KEY = "bcpl_build_reload_for";
  const MIN_GAP_MS = 10 * 60 * 1000;
  let reloading = false;
  let lastCheckAt = 0;
  let debounceTimer = 0;

  const check = async () => {
    if (reloading || document.hidden) return;
    const now = Date.now();
    if (lastCheckAt && now - lastCheckAt < MIN_GAP_MS) return;
    lastCheckAt = now;
    try {
      const res = await fetch(`${process.env.PUBLIC_URL || ""}/build-meta.json`, {
        cache: "no-store",
        headers: { Accept: "application/json" },
      });
      if (!res.ok) return;
      const meta = await res.json();
      const nextId = meta?.buildId != null ? String(meta.buildId) : "";
      if (!nextId) return;

      if (nextId === String(currentBuildId)) {
        try {
          sessionStorage.removeItem(RELOAD_KEY);
        } catch {
          /* private mode */
        }
        return;
      }

      let alreadyTried = "";
      try {
        alreadyTried = sessionStorage.getItem(RELOAD_KEY) || "";
      } catch {
        /* private mode */
      }
      if (alreadyTried === nextId) {
        return;
      }

      try {
        sessionStorage.setItem(RELOAD_KEY, nextId);
      } catch {
        /* private mode */
      }
      reloading = true;
      window.location.reload();
    } catch {
      /* offline / transient — ignore */
    }
  };

  const schedule = () => {
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(check, 1500);
  };

  window.addEventListener("focus", schedule);
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) schedule();
  });
  window.setTimeout(check, 8000);
}

/** @deprecated use registerPushServiceWorker */
export function register() {
  return registerPushServiceWorker();
}
